<script setup lang="ts">
import { useStore } from "vuex";
import { DsIconButton, DsButton } from "@coloplast/design-system";
import { FormKit } from '@formkit/vue';
import { computed, ref, watch } from "vue";
import { type AxiosError } from "axios";

export interface Props {
	labels: {
		couponAppliedHeaderLabel: string;
		couponHeaderLabel: string;
		couponValidationErrorMessage: string;
		applyCouponButtonLabel: string;
		accessibility: {
			removeCouponCode: string;
		}
	}
}

const props = defineProps<Props>();

const store = useStore();

const errorMessage = ref('');
const couponName = computed<string>({
	get: () => store.getters.getDiscountCode.couponName,
	set: (val) => {
		store.commit('setDiscountCode', {
			couponName: val,
		});
	}
});

const culture = computed(() => store.getters.getCulture);
const basket = computed(() => store.getters.getBasket);
/**
 * `isCouponApplied` in basket response is currently true only for order discount.
 * That's why need to use `appliedCouponCode` to determine if user applied code in this component.
 * It ensures that item level discount coupon can be removed and user cannot use multiple codes.
 */
const isCouponApplied = computed<boolean>(() => !!basket.value.appliedCouponCode);
const inputLabel = computed<string>(() => {
	return isCouponApplied.value
		? props.labels.couponAppliedHeaderLabel
		: props.labels.couponHeaderLabel;
});

const inputErrors = computed<string[]>(() => {
	return errorMessage.value ? [errorMessage.value] : [];
});

const isApplyButtonDisabled = computed<boolean>(() => couponName.value === '');

function handleInput(val: string) {
	couponName.value = val;
	errorMessage.value = '';
}

async function handleSubmit() {
	errorMessage.value = '';

	const payload = {
		language: culture.value,
		CouponCode: couponName.value.toUpperCase(),
	};
	try {
		const response = await store.dispatch('applyCouponBasket', payload);
		if(!response.data && response.status !== 200) {
			throw new Error();
		}
	}
	catch (error) {
		errorMessage.value = (error as AxiosError<string>)?.response?.data || props.labels.couponValidationErrorMessage;
	}
}

async function removeCouponCode() {
	errorMessage.value = '';

	const payload = {
		language: culture.value,
		CouponCode: couponName.value,
	};
	try {
		await store.dispatch('removeCouponBasket', payload);
	}
	catch(error) {
		const message = (error as AxiosError<string>)?.response?.data;
		if (message) {
			errorMessage.value = message;
		}
	}
}

watch(() => basket.value.appliedCouponCode, () => {
	couponName.value = basket.value.appliedCouponCode || '';
	errorMessage.value = '';
}, {
	immediate: true,
});
</script>

<template>
	<FormKit
		type="form"
		:actions="false"
		:classes="{
			form: {
				'c-discount-code': true,
				'c-discount-code--applied': isCouponApplied,
			}
		}"
		@submit="handleSubmit"
	>
		<FormKit
			:model-value="couponName"
			type="text"
			:label="inputLabel"
			autocomplete="off"
			:readonly="isCouponApplied"
			:classes="{
				outer: 'c-discount-code__textfield',
			}"
			:errors="inputErrors"
			data-testid="discount-code-input"
			@input="handleInput"
		/>
		<div class="c-discount-code__actions">
			<DsIconButton
				v-if="isCouponApplied"
				variant="ghost"
				icon-name="delete"
				:ariaLabel="labels.accessibility.removeCouponCode"
				@click.prevent="removeCouponCode"
				data-testid="discount-code-remove"
			/>
			<DsButton
				v-else
				type="submit"
				variant="tertiary"
				:disabled="isApplyButtonDisabled"
				data-testid="discount-code-check-discount"
			>
				{{ labels.applyCouponButtonLabel }}
			</DsButton>
		</div>
	</FormKit>
</template>
